<!--
  WhereUsedPanel.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT,
  Phase 3.2 -- same card).

  SHARED COMPONENT contract. Used by:
    - Phase 2 Approval Workspace -- detail panel "Where this is used."
      Flat list rendering (``group-by="none"``, the default).
    - Phase 3 Decoration Detail page -- "Where used" panel grouped by
      store (``group-by="store"``).

  Keep the public API minimal. Consumers pass:

      :usage="{ campaign_products: [...], stores: [...] }"
      :sellable="<bool>"   -- when false, render the "not yet sellable"
                              footnote (the artwork is still in flight,
                              its placements show in the lists but
                              storefronts won't surface them).
      :group-by="none|store"
                            -- "none" (default): flat list, one row per
                                  campaign_product. Phase 2 contract.
                            -- "store": campaign_products grouped under
                                  per-store headers with the store name
                                  and per-store product count. Phase 3
                                  Decoration Detail.
      :loading="<bool>"     -- in-flight indicator for first load.

  Backing endpoint: GET /api/artworks/<id>/usage. The Pinia store's
  ``fetchUsage(artworkId)`` does the call and caches per session.

  Adding optional keys to the contract is safe; renaming or removing
  existing keys is a breaking change that must be coordinated across
  both surfaces. Adding new ``group-by`` modes is additive.

  Empty state: matter-of-fact -- "This artwork is not currently placed
  on any product."
-->
<template>
    <div class="aw-usage" data-testid="where-used-panel">
        <div class="aw-usage__header">
            <span class="aw-usage__title">{{ titleText }}</span>
            <span
                v-if="storesCount > 0 || productsCount > 0"
                class="aw-usage__counts"
                >{{ productsCount }} product{{
                    productsCount === 1 ? '' : 's'
                }}
                · {{ storesCount }} store{{
                    storesCount === 1 ? '' : 's'
                }}</span
            >
        </div>
        <div
            v-if="loading && !campaignProducts.length"
            class="aw-usage__loading"
            data-testid="where-used-loading"
        >
            Loading product placements...
        </div>
        <div
            v-else-if="!campaignProducts.length"
            class="aw-usage__empty"
            data-testid="where-used-empty"
        >
            This artwork is not currently placed on any product.
        </div>
        <!--
          Flat rendering: Phase 2 Approval Workspace contract. Preserved
          verbatim so the existing Vitest contract continues to pass.
        -->
        <ul
            v-else-if="groupBy !== 'store'"
            class="aw-usage__list"
        >
            <li
                v-for="cp in campaignProducts"
                :key="cp.id"
                class="aw-usage__row"
                :data-testid="`where-used-row-${cp.id}`"
            >
                <span class="aw-usage__thumb" aria-hidden="true">
                    <img
                        v-if="cp.image_url"
                        :src="cp.image_url"
                        :alt="cp.name || ''"
                    />
                    <span v-else>{{ thumbLetter(cp.name) }}</span>
                </span>
                <span class="aw-usage__row-body">
                    <span class="aw-usage__product-name">{{
                        cp.name || `Product ${cp.id}`
                    }}</span>
                    <span class="aw-usage__store-name">{{
                        cp.store_name || `Store ${cp.store_id}`
                    }}</span>
                </span>
                <span
                    class="aw-usage__decoration-count"
                    :title="`${cp.decoration_count} placement(s)`"
                    >{{ cp.decoration_count }}
                    placement{{
                        cp.decoration_count === 1 ? '' : 's'
                    }}</span
                >
            </li>
        </ul>
        <!--
          Grouped-by-store rendering: Phase 3 Decoration Detail. The
          design package calls for a per-store header with the store
          name and per-store product count, followed by the store's
          campaign_products as inset rows (no thumb -- the detail page
          already shows the artwork thumb in the header).
        -->
        <div
            v-else
            class="aw-usage__groups"
        >
            <section
                v-for="group in groupedByStore"
                :key="group.store_id"
                class="aw-usage__group"
                :data-testid="`where-used-group-${group.store_id}`"
            >
                <header class="aw-usage__group-header">
                    <span class="aw-usage__group-store-name">{{
                        group.store_name ||
                        `Store ${group.store_id}`
                    }}</span>
                    <span class="aw-usage__group-count"
                        >{{ group.products.length }} product{{
                            group.products.length === 1 ? '' : 's'
                        }}</span
                    >
                </header>
                <ul class="aw-usage__list aw-usage__list--grouped">
                    <li
                        v-for="cp in group.products"
                        :key="cp.id"
                        class="aw-usage__row aw-usage__row--grouped"
                        :data-testid="`where-used-row-${cp.id}`"
                    >
                        <span class="aw-usage__row-body">
                            <span class="aw-usage__product-name">{{
                                cp.name || `Product ${cp.id}`
                            }}</span>
                        </span>
                        <span
                            class="aw-usage__decoration-count"
                            :title="`${cp.decoration_count} placement(s)`"
                            >{{ cp.decoration_count }}
                            placement{{
                                cp.decoration_count === 1 ? '' : 's'
                            }}</span
                        >
                    </li>
                </ul>
            </section>
        </div>
        <p
            v-if="!sellable && campaignProducts.length"
            class="aw-usage__note"
            data-testid="where-used-not-sellable-note"
        >
            These products can be configured now, but won't be sellable
            until this artwork is approved.
        </p>
    </div>
</template>

<script>
export default {
    name: 'WhereUsedPanel',
    props: {
        usage: {
            // { campaign_products: [...], stores: [...] }
            type: Object,
            default: () => ({ campaign_products: [], stores: [] }),
        },
        sellable: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Layout mode.
         *
         *   "none"  -- flat list (Phase 2 Approval Workspace).
         *   "store" -- grouped under per-store headers (Phase 3
         *              Decoration Detail).
         *
         * Future modes (e.g. "client") are additive; existing values
         * are stable contract.
         */
        groupBy: {
            type: String,
            default: 'none',
            validator(value) {
                return value === 'none' || value === 'store'
            },
        },
        /**
         * Phase 3.2: section header override. The Phase 2 Approval
         * Workspace uses "Where this is used"; the Phase 3 Decoration
         * Detail page uses the shorter "Where used" to match the
         * design package. Defaults to the Phase 2 string so the Phase
         * 2 contract is preserved.
         */
        title: {
            type: String,
            default: 'Where this is used',
        },
    },
    computed: {
        campaignProducts() {
            return this.usage?.campaign_products || []
        },
        stores() {
            return this.usage?.stores || []
        },
        productsCount() {
            return this.campaignProducts.length
        },
        storesCount() {
            return this.stores.length
        },
        titleText() {
            return this.title
        },
        /**
         * Build the per-store buckets in stable order. We iterate
         * campaign_products (server-sorted by store_name asc then
         * product_name asc per the /usage endpoint) so the rendered
         * order matches the server contract -- a store's first
         * appearance in the campaign_products list determines that
         * store's group position.
         */
        groupedByStore() {
            const byStore = new Map()
            for (const cp of this.campaignProducts) {
                const key = cp.store_id
                if (!byStore.has(key)) {
                    byStore.set(key, {
                        store_id: cp.store_id,
                        store_name: cp.store_name,
                        products: [],
                    })
                }
                byStore.get(key).products.push(cp)
            }
            return Array.from(byStore.values())
        },
    },
    methods: {
        thumbLetter(name) {
            const s = (name || 'P').replace(/[^A-Za-z]/g, '')
            return (s[0] || 'P').toUpperCase()
        },
    },
}
</script>

<style scoped>
.aw-usage {
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    background: var(--tf-bg, #ffffff);
    margin-bottom: 16px;
}
.aw-usage__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: var(--tf-surface-alt, #f7fafc);
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
    font-size: 12px;
}
.aw-usage__title {
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
}
.aw-usage__counts {
    color: var(--tf-fg-4, #5a6a82);
}
.aw-usage__loading,
.aw-usage__empty {
    padding: 20px 14px;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 13px;
    text-align: center;
}
.aw-usage__list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.aw-usage__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--tf-border, #f1f5f9);
}
.aw-usage__row:last-child {
    border-bottom: none;
}
.aw-usage__thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 4px;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-3, #4a5568);
    font-weight: 600;
    font-size: 12px;
    overflow: hidden;
}
.aw-usage__thumb img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
.aw-usage__row-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}
.aw-usage__product-name {
    font-size: 13px;
    color: var(--tf-fg-1, #1a202c);
}
.aw-usage__store-name {
    font-size: 11px;
    color: var(--tf-fg-4, #5a6a82);
}
.aw-usage__decoration-count {
    font-size: 11px;
    color: var(--tf-fg-4, #5a6a82);
}
.aw-usage__note {
    padding: 8px 14px;
    margin: 0;
    font-size: 11px;
    color: var(--tf-fg-4, #5a6a82);
    background: var(--tf-surface-alt, #f7fafc);
    border-top: 1px solid var(--tf-border, #f1f5f9);
}
/* Phase 3.2: grouped-by-store rendering */
.aw-usage__groups {
    padding: 0;
}
.aw-usage__group:not(:last-child) {
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
}
.aw-usage__group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: var(--tf-surface-alt, #f7fafc);
    font-size: 12px;
    border-bottom: 1px solid var(--tf-border, #f1f5f9);
}
.aw-usage__group-store-name {
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
}
.aw-usage__group-count {
    color: var(--tf-fg-4, #5a6a82);
}
.aw-usage__list--grouped {
    padding-left: 14px;
}
.aw-usage__row--grouped {
    padding-left: 0;
}
</style>
